import { useCallback, useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';

import * as yup from 'yup';
import { i18n as I18n } from 'utils/i18n';

// Hooks
import useValidation from 'hooks/useValidation';
import { useApiRequest } from 'hooks/useApiRequest';

import { PASSWORD_REGEX } from '../../constants/password';
import { phoneNumber, validateXSS, xssErrorMessage } from '../../validators';

export const useUserDetailsFormHook = ({
  fields,
  handleFieldChange,
  handleSubmitCallback,
  schemaOverRides,
  validateEmailUniqueness,
}) => {
  const schema = useMemo(() => {
    return yup.object().shape({
      firstName: yup
        .string()
        .required(
          I18n.t('validations.required', {
            name: I18n.t('registration.user_details.fields.first_name.label'),
          })
        )
        .test('no-xss', xssErrorMessage, value => validateXSS(value)),
      lastName: yup
        .string()
        .required(
          I18n.t('validations.required', {
            name: I18n.t('registration.user_details.fields.last_name.label'),
          })
        )
        .test('no-xss', xssErrorMessage, value => validateXSS(value)),
      phoneNumber: phoneNumber(
        I18n.t('registration.user_details.fields.phone_number.label')
      ),
      mobileNumber: phoneNumber(
        I18n.t('registration.user_details.fields.mobile_number.label')
      ),
      email: yup
        .string()
        .email(
          I18n.t('validations.email', {
            name: I18n.t('registration.user_details.fields.email.label'),
          })
        )
        .test('no-xss', xssErrorMessage, value => validateXSS(value))
        .required(
          I18n.t('validations.required', {
            name: I18n.t('registration.user_details.fields.email.label'),
          })
        ),
      confirmEmail: yup
        .string()
        .email(
          I18n.t('validations.email', {
            name: I18n.t(
              'registration.user_details.fields.confirm_email.label'
            ),
          })
        )
        .test('no-xss', xssErrorMessage, value => validateXSS(value))
        .required(
          I18n.t('validations.required', {
            name: I18n.t(
              'registration.user_details.fields.confirm_email.label'
            ),
          })
        )
        .test(
          'matchesEmail',
          I18n.t('validations.match', {
            name: I18n.t(
              'registration.user_details.fields.confirm_email.label'
            ),
          }),
          /* eslint-disable func-names */
          function (confirmValue) {
            const value = this.resolve(yup.ref('email'));
            return !value || !confirmValue || value === confirmValue;
          }
          /* eslint-enable func-names */
        ),
      password: yup
        .string()
        .required(
          I18n.t('validations.required', {
            name: I18n.t('registration.user_details.fields.password.label'),
          })
        )
        .test('no-xss', xssErrorMessage, value => validateXSS(value))
        .matches(
          PASSWORD_REGEX,
          I18n.t('validations.password_security', {
            name: I18n.t('registration.user_details.fields.password.label'),
            length: 10,
          })
        ),
      confirmPassword: yup
        .string()
        .required(
          I18n.t('validations.required', {
            name: I18n.t(
              'registration.user_details.fields.confirm_password.label'
            ),
          })
        )
        .test('no-xss', xssErrorMessage, value => validateXSS(value))
        .test(
          'matchesPassword',
          I18n.t('validations.match', {
            name: I18n.t(
              'registration.user_details.fields.confirm_password.label'
            ),
          }),
          /* eslint-disable func-names */
          function (confirmValue) {
            const value = this.resolve(yup.ref('password'));
            return !value || !confirmValue || value === confirmValue;
          }
          /* eslint-enable func-names */
        ),
      marketingOptions: yup.array().of(yup.string()),
      ...schemaOverRides,
    });
  }, [schemaOverRides]);

  const {
    errors,
    setErrors,
    handleChange: handleFieldValidation,
    isValid,
    validate,
  } = useValidation(fields, schema);

  const [userIsValid, setUserIsValid] = useState(false);
  const [emailIsUnregistered, setEmailIsUnregistered] = useState(false);
  const [marketingOptions, setMarketingOptions] = useState([]);

  const apiRequest = useApiRequest();

  useEffect(() => {
    const fetchMarketingOptions = async () => {
      if (marketingOptions.length === 0) {
        const { data } = await apiRequest.get(
          '/v1/organisations/marketing_options'
        );
        setMarketingOptions(data);
      }
    };

    fetchMarketingOptions();
  }, [marketingOptions, apiRequest]);

  const checkMatch = useCallback(
    (fieldName, value, checkValue) => {
      if (value !== '' && checkValue !== '') {
        handleFieldValidation({
          currentTarget: {
            name: fieldName,
            value,
          },
        });
      }
    },
    [handleFieldValidation]
  );

  useEffect(() => {
    checkMatch('email', fields.email, fields.confirmEmail);
  }, [checkMatch, fields.email, fields.confirmEmail]);

  useEffect(() => {
    checkMatch('confirmEmail', fields.confirmEmail, fields.email);
  }, [checkMatch, fields.email, fields.confirmEmail]);

  useEffect(() => {
    checkMatch('password', fields.password, fields.confirmPassword);
  }, [checkMatch, fields.password, fields.confirmPassword]);

  useEffect(() => {
    checkMatch('confirmPassword', fields.confirmPassword, fields.password);
  }, [checkMatch, fields.password, fields.confirmPassword]);

  const checkUniqueEmail = useCallback(
    email => {
      if (isValid()) {
        const emailIsRegistered = async () => {
          await apiRequest.get('/v1/users/email/', { params: { find: email } });
        };

        emailIsRegistered()
          .then(() => {
            setErrors(prev => ({
              ...prev,
              email: I18n.t('validations.email_registered'),
            }));
            setEmailIsUnregistered(false);
          })
          .catch(() => {
            setEmailIsUnregistered(true);
          });
      }
    },
    [isValid, apiRequest, setErrors]
  );

  useEffect(() => {
    if (validateEmailUniqueness) {
      checkUniqueEmail(fields.email);
    }
  }, [fields, checkUniqueEmail, validateEmailUniqueness]);

  useEffect(() => {
    if (validateEmailUniqueness) {
      setUserIsValid(isValid() && emailIsUnregistered);
    } else {
      setUserIsValid(isValid());
    }
  }, [isValid, emailIsUnregistered, validateEmailUniqueness]);

  const handleChange = event => {
    handleFieldChange(event);
    handleFieldValidation(event);
  };

  const handlePasswordChange = value => {
    const event = { currentTarget: { name: 'password', value } };
    handleFieldChange(event);
    handleFieldValidation(event);
  };

  const handleSubmit = e => {
    e.preventDefault();
    if (isValid()) {
      handleSubmitCallback();
    }

    return false;
  };

  return {
    errors,
    handleSubmit,
    handlePasswordChange,
    handleChange,
    checkMatch,
    marketingOptions,
    isValid: userIsValid,
    isFieldsValid: isValid,
    validate,
  };
};

useUserDetailsFormHook.propTypes = {
  fields: PropTypes.object.isRequired,
  handleFieldChange: PropTypes.func.isRequired,
  handleSubmitCallback: PropTypes.func,
};

useUserDetailsFormHook.defaultProps = {
  handleSubmitCallback: () => null,
};
